import React from 'react';
import { Button } from '@rsos/base-components';
import { useCurrentPSAP } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { StyledDescription, StyledWarning } from './TextTo911DebugPanel.styles';

const TRANSFER_STATUSES = ['pending', 'accepted', 'timed_out'] as const;

interface MockTransferControlsProps {
  callerID: string;
}

/**
 * Creates and dispatches a mock conversation.transfer message on the StreamWS
 * handle with the given callerID and transfer status. This is used to simulate
 * incoming transfer messages for testing the frontend's handling of different
 * transfer statuses. This is a **frontend-only mock and will not be reflected
 * on the backend.**
 */
function dispatchMockTransfer(
  callerID: string,
  currentUserAuthorityId: string | undefined,
  transferStatus: (typeof TRANSFER_STATUSES)[number],
) {
  const handle = window.__RSOS?.streamWS?.handle;

  if (!handle) {
    // eslint-disable-next-line no-console -- debug panel
    console.warn('StreamWS handle not available');
    return;
  }

  handle({
    type:
      transferStatus == 'pending'
        ? 'conversation.transfer'
        : 'conversation.transfer.update',
    // Required by normalizeMessage in StreamWS.js which checks
    // topic.startsWith('tel://') — empty string passes through unchanged.
    topic: '',
    body: {
      caller_id: callerID,
      conversation_id: `mock-transfer-${Date.now()}`,
      transfer_status: transferStatus,
      receiving_authority_display_name: 'Mock Agency',
      receiving_authority_account_id: 'mock-agency-account-id',
      initiating_authority_display_name: 'Mock Initiating Agency',
      initiating_authority_account_id: currentUserAuthorityId,
    },
    source: 'tcapi',
  });
}

export function MockTransferControls({ callerID }: MockTransferControlsProps) {
  const currentUserAuthority = useCurrentPSAP();
  const currentUserAuthorityId = currentUserAuthority?.account_id;
  if (!currentUserAuthorityId) {
    throw new Error(
      "Current user's authority account ID is required for MockTransferControls",
    );
  }

  return (
    <div style={{ marginTop: '12px' }}>
      <StyledDescription>
        Mock a conversation transfer status:
      </StyledDescription>
      <StyledWarning>
        Frontend-only mock. This will not be reflected on the backend.
      </StyledWarning>
      <div style={{ display: 'flex', gap: '8px' }}>
        {TRANSFER_STATUSES.map(status => (
          <Button
            key={status}
            onClick={() =>
              dispatchMockTransfer(callerID, currentUserAuthorityId, status)
            }
            btnType="secondary"
          >
            <strong>{status}</strong>
          </Button>
        ))}
      </div>
    </div>
  );
}
